import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, CircularProgress, Tooltip, Switch } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import BusinessIcon from '@mui/icons-material/Business';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BlockIcon from '@mui/icons-material/Block';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import EntityHistoryButton from '../components/common/EntityHistoryButton';
import { ConfirmDialog } from '../components/common';

import './EntityManagement.css';

interface Tenant {
  id: string;
  name: string;
  domain: string;
  status: string;
  createdAt: string;
}

const TenantManagement: React.FC = () => {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Dialogs
  const [openDialog, setOpenDialog] = useState(false);
  const [currentTenant, setCurrentTenant] = useState<Tenant | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [tenantToDelete, setTenantToDelete] = useState<Tenant | null>(null);

  // Form
  const [formName, setFormName] = useState('');
  const [formDomain, setFormDomain] = useState('');
  const [formIsActive, setFormIsActive] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [formError, setFormError] = useState('');

  const fetchTenants = () => {
    setLoading(true);
    const mockTenants: Tenant[] = [
      { id: '1', name: 'سازمان الف', domain: 'org-a.example.com', status: 'فعال', createdAt: '1402/02/15' },
      { id: '2', name: 'شرکت ب', domain: 'company-b.example.com', status: 'فعال', createdAt: '1402/03/21' },
      { id: '3', name: 'فروشگاه ج', domain: 'store-c.example.com', status: 'غیرفعال', createdAt: '1402/05/10' }
    ];
    setTimeout(() => {
      setTenants(mockTenants);
      setLoading(false);
    }, 500);
  };

  useEffect(() => {
    fetchTenants();
  }, []);

  const handleSearch = () => setSearchTerm(searchInput);
  const handleKeyPress = (e: React.KeyboardEvent) => { if (e.key === 'Enter') handleSearch(); };

  const handleOpenDialog = (tenant: Tenant | null) => {
    setCurrentTenant(tenant);
    if (tenant) {
      setFormName(tenant.name);
      setFormDomain(tenant.domain);
      setFormIsActive(tenant.status === 'فعال');
    } else {
      setFormName('');
      setFormDomain('');
      setFormIsActive(true);
    }
    setFormError('');
    setOpenDialog(true);
  };

  const handleCloseDialog = () => { setOpenDialog(false); setCurrentTenant(null); };

  const handleSaveTenant = () => {
    if (!formName) { setFormError('لطفا نام مستاجر را وارد کنید'); return; }
    if (!formDomain) { setFormError('لطفا دامنه را وارد کنید'); return; }
    setSubmitLoading(true);
    setTimeout(() => {
      setSubmitLoading(false);
      handleCloseDialog();
      fetchTenants();
    }, 500);
  };

  const handleDeleteDialogOpen = (tenant: Tenant) => { setTenantToDelete(tenant); setDeleteDialogOpen(true); };
  const handleDeleteDialogClose = () => { setDeleteDialogOpen(false); setTenantToDelete(null); };
  const handleDeleteTenant = () => {
    if (!tenantToDelete) return;
    setSubmitLoading(true);
    setTimeout(() => {
      setTenants(tenants.filter(t => t.id !== tenantToDelete.id));
      setSubmitLoading(false);
      handleDeleteDialogClose();
    }, 500);
  };

  const clearFilters = () => { setSearchTerm(''); setSearchInput(''); };

  const filteredTenants = tenants.filter(t =>
    !searchTerm || t.name.includes(searchTerm) || t.domain.includes(searchTerm)
  );

  const totalCount = filteredTenants.length;
  const totalPages = Math.ceil(totalCount / rowsPerPage);
  const startItem = (page - 1) * rowsPerPage + 1;
  const endItem = Math.min(page * rowsPerPage, totalCount);
  const pagedTenants = filteredTenants.slice((page - 1) * rowsPerPage, page * rowsPerPage);

  const activeTenants = tenants.filter(t => t.status === 'فعال').length;
  const inactiveTenants = tenants.filter(t => t.status !== 'فعال').length;

  return (
    <div className="entity-page">
      {/* Header */}
      <div className="entity-header">
        <div>
          <h1 className="entity-title">مدیریت مستاجران</h1>
          <p className="entity-subtitle">مدیریت سازمان‌ها و دامنه‌های اختصاصی</p>
        </div>
        <div className="header-actions">
          <button className="header-btn refresh" onClick={fetchTenants} title="بروزرسانی">
            <RefreshIcon fontSize="small" />
          </button>
          <button className="header-btn primary" onClick={() => handleOpenDialog(null)}>
            <AddIcon fontSize="small" />
            افزودن مستاجر
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="entity-stats">
        <div className="entity-stat-card">
          <div className="entity-stat-icon blue"><BusinessIcon /></div>
          <div>
            <p className="entity-stat-label">کل مستاجران</p>
            <p className="entity-stat-value">{tenants.length}</p>
          </div>
        </div>
        <div className="entity-stat-card">
          <div className="entity-stat-icon green"><CheckCircleIcon /></div>
          <div>
            <p className="entity-stat-label">فعال</p>
            <p className="entity-stat-value">{activeTenants}</p>
          </div>
        </div>
        <div className="entity-stat-card">
          <div className="entity-stat-icon red"><BlockIcon /></div>
          <div>
            <p className="entity-stat-label">غیرفعال</p>
            <p className="entity-stat-value">{inactiveTenants}</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="entity-filters">
        <div className="filters-grid">
          <div className="filter-group">
            <label className="filter-label">
              <SearchIcon fontSize="small" />
              جستجو
            </label>
            <div className="filter-search-wrap">
              <input
                className="filter-field"
                type="text"
                placeholder="جستجوی مستاجر..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyPress={handleKeyPress}
              />
              <span className="filter-search-icon">
                <SearchIcon fontSize="small" />
              </span>
            </div>
          </div>

          <div className="filter-group filter-group-actions">
            <button className="filter-btn" onClick={handleSearch}>
              <SearchIcon fontSize="small" />
              اعمال
            </button>
            {searchTerm && (
              <button className="filter-btn outline" onClick={clearFilters}>
                پاک
              </button>
            )}
          </div>
        </div>

        {searchTerm && (
          <div className="filters-active">
            <span className="filters-label">فیلترهای فعال:</span>
            <span className="filter-tag">
              جستجو: {searchTerm}
              <button onClick={() => { setSearchTerm(''); setSearchInput(''); }}>×</button>
            </span>
            <button className="filters-clear" onClick={clearFilters}>پاک کردن همه</button>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="entity-table-wrapper">
        {loading ? (
          <div className="entity-loading"><CircularProgress size={32} /></div>
        ) : filteredTenants.length === 0 ? (
          <div className="entity-empty">
            <div className="entity-empty-icon"><BusinessIcon style={{ fontSize: 48 }} /></div>
            <p className="entity-empty-text">هیچ مستاجری یافت نشد</p>
          </div>
        ) : (
          <>
            <table className="entity-table">
              <thead>
                <tr>
                  <th>مستاجر</th>
                  <th>دامنه</th>
                  <th>وضعیت</th>
                  <th>تاریخ ایجاد</th>
                  <th style={{ width: 140 }}>عملیات</th>
                </tr>
              </thead>
              <tbody>
                {pagedTenants.map((tenant) => (
                  <tr key={tenant.id}>
                    <td>
                      <div className="entity-info-cell">
                        <div className="entity-image">
                          <BusinessIcon />
                        </div>
                        <div className="entity-info">
                          <span className="entity-name">{tenant.name}</span>
                        </div>
                      </div>
                    </td>
                    <td><span className="cell-code" dir="ltr">{tenant.domain}</span></td>
                    <td>
                      <span className={`tag tag-sm ${tenant.status === 'فعال' ? 'tag-active' : 'tag-inactive'}`}>
                        {tenant.status}
                      </span>
                    </td>
                    <td>{tenant.createdAt}</td>
                    <td>
                      <div className="actions-cell">
                        <Tooltip title="ویرایش" arrow>
                          <button className="action-btn edit" onClick={() => handleOpenDialog(tenant)}>
                            <EditIcon />
                          </button>
                        </Tooltip>
                        <Tooltip title="حذف" arrow>
                          <button className="action-btn delete" onClick={() => handleDeleteDialogOpen(tenant)}>
                            <DeleteIcon />
                          </button>
                        </Tooltip>
                        <EntityHistoryButton
                          entityType="Tenant"
                          entityId={tenant.id}
                          size="small"
                          variant="text"
                          canRestore={true}
                          onVersionRestored={fetchTenants}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Mobile Cards */}
            <div className="entity-cards">
              {pagedTenants.map((tenant) => (
                <div key={tenant.id} className={`entity-card ${tenant.status !== 'فعال' ? 'inactive' : ''}`}>
                  <div className="entity-card-header">
                    <div className="entity-card-image"><BusinessIcon /></div>
                    <div className="entity-card-info">
                      <div className="entity-card-name">{tenant.name}</div>
                      <div className="entity-card-desc" dir="ltr">{tenant.domain}</div>
                    </div>
                    <div className="entity-card-status">
                      <span className={`tag tag-sm ${tenant.status === 'فعال' ? 'tag-active' : 'tag-inactive'}`}>
                        {tenant.status}
                      </span>
                    </div>
                  </div>
                  <div className="entity-card-actions">
                    <button className="entity-card-btn primary" onClick={() => handleOpenDialog(tenant)}>
                      <EditIcon fontSize="small" /> ویرایش
                    </button>
                    <button className="entity-card-btn danger" onClick={() => handleDeleteDialogOpen(tenant)}>
                      <DeleteIcon fontSize="small" /> حذف
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            <div className="entity-pagination">
              <div className="pagination-info">
                نمایش {startItem} تا {endItem} از {totalCount}
              </div>
              <div className="pagination-controls">
                <select
                  className="pagination-select"
                  value={rowsPerPage}
                  onChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(1); }}
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
                <button className="pagination-btn" onClick={() => setPage(page + 1)} disabled={page >= totalPages}>
                  <ChevronRightIcon fontSize="small" />
                </button>
                <span style={{ fontSize: 12 }}>{page} / {totalPages || 1}</span>
                <button className="pagination-btn" onClick={() => setPage(page - 1)} disabled={page <= 1}>
                  <ChevronLeftIcon fontSize="small" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth className="tenant-edit-dialog">
        <div className="edit-dialog-title">
          <span>{currentTenant ? 'ویرایش مستاجر' : 'افزودن مستاجر جدید'}</span>
          <button className="details-close" onClick={handleCloseDialog}>
            <CloseIcon fontSize="small" />
          </button>
        </div>
        <DialogContent className="edit-dialog-content">
          {formError && <div className="dialog-error">{formError}</div>}
          <div className="edit-form">
            {/* Basic Info */}
            <div className="edit-card">
              <div className="edit-card-header">اطلاعات اصلی</div>
              <div className="edit-card-body">
                <div className="edit-form-grid">
                  <div className="edit-field full">
                    <label>نام مستاجر <span style={{ color: '#dc2626' }}>*</span></label>
                    <input
                      type="text"
                      value={formName}
                      onChange={(e) => setFormName(e.target.value)}
                      placeholder="مثلا: سازمان الف"
                    />
                  </div>
                  <div className="edit-field full">
                    <label>دامنه <span style={{ color: '#dc2626' }}>*</span></label>
                    <input
                      type="text"
                      value={formDomain}
                      onChange={(e) => setFormDomain(e.target.value)}
                      placeholder="مثلا: org-a.example.com"
                      dir="ltr"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Settings */}
            <div className="edit-card">
              <div className="edit-card-header">تنظیمات</div>
              <div className="edit-card-body">
                <div className="edit-switches">
                  <label className="edit-switch-item">
                    <div>
                      <div className="edit-switch-label">وضعیت فعال</div>
                      <div className="edit-switch-desc">مستاجر به سیستم دسترسی داشته باشد</div>
                    </div>
                    <Switch size="small" checked={formIsActive} onChange={(e) => setFormIsActive(e.target.checked)} />
                  </label>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
        <div className="edit-dialog-actions">
          <button className="edit-btn cancel" onClick={handleCloseDialog}>انصراف</button>
          <button className="edit-btn submit" onClick={handleSaveTenant} disabled={submitLoading}>
            {submitLoading && <CircularProgress size={16} style={{ color: '#fff' }} />}
            {currentTenant ? 'ذخیره تغییرات' : 'افزودن مستاجر'}
          </button>
        </div>
      </Dialog>

      {/* Delete Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        title="حذف مستاجر"
        message={
          <span>
            آیا از حذف مستاجر <strong>«{tenantToDelete?.name}»</strong> اطمینان دارید؟
            <br />این عملیات غیرقابل بازگشت است.
          </span>
        }
        cancelText="انصراف"
        confirmText="حذف مستاجر"
        confirmColor="error"
        loading={submitLoading}
        onCancel={handleDeleteDialogClose}
        onConfirm={handleDeleteTenant}
      />
    </div>
  );
};

export default TenantManagement;
